import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from '@mui/material';
import { useAuthStore } from '../../stores/root_store';
import { managerApi, type ManagerUserUpdate } from '../../api/manager_api';
import { useManagerUser } from './ManagerUserPage';
import { ManagerUserBannedAssistants } from './ManagerUserBannedAssistants';

// Default open-task cap when allowed_open_tasks is 0 (models/user.py)
const DEFAULT_ALLOWED_OPEN_TASKS = 3;

// Roles that sudo users can edit (excludes sudo itself). Manager, mentor and
// auto-mentor live under "Assistant Flags" instead.
const EDITABLE_ROLES = [
  'client',
  'assistant',
  'support',
  'trusted-tester',
  'dedicated',
];

// Human-readable labels for roles
const ROLE_LABELS: Record<string, string> = {
  'client': 'Client',
  'assistant': 'Assistant',
  'support': 'Support',
  'trusted-tester': 'Trusted Tester',
  'manager': 'Manager',
  'mentor': 'Mentor',
  'auto-mentor': 'Auto-Mentor',
  'dedicated': 'Dedicated Assistant',
  'sudo': 'Sudo',
};

// Color mapping for role chips
const getRoleColor = (role: string): 'error' | 'warning' | 'primary' | 'secondary' | 'success' | 'default' => {
  switch (role) {
    case 'sudo': return 'error';
    case 'manager': return 'warning';
    case 'support': return 'primary';
    case 'mentor':
    case 'auto-mentor': return 'secondary';
    case 'dedicated': return 'success';
    default: return 'default';
  }
};

export const ManagerUserEdit = observer(() => {
  const { userKey } = useParams<{ userKey: string }>();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const { user, setUser, error, setError } = useManagerUser();

  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [isBeta, setIsBeta] = useState(false);
  const [isIc, setIsIc] = useState(false);
  const [isMentor, setIsMentor] = useState(false);
  const [autoMentor, setAutoMentor] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [allowedOpenTasks, setAllowedOpenTasks] = useState('0');
  const [clearingCooldown, setClearingCooldown] = useState(false);

  // Role editing state
  const [savingRole, setSavingRole] = useState<string | null>(null);

  // Check if current user has sudo role (can edit roles)
  const currentUserHasSudo = authStore.roles.includes('sudo');

  // Initialize form when user loads
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.primary_email || '');
      setIsDisabled(user.is_disabled);
      setIsBeta(user.is_beta);
      setIsIc(user.is_ic);
      setIsMentor(user.is_mentor);
      setAutoMentor(user.has_auto_mentor);
      setIsManager(user.has_manager);
      setAllowedOpenTasks(String(user.allowed_open_tasks ?? 0));
    }
  }, [user]);

  const resetForm = () => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.primary_email || '');
      setIsDisabled(user.is_disabled);
      setIsBeta(user.is_beta);
      setIsIc(user.is_ic);
      setIsMentor(user.is_mentor);
      setAutoMentor(user.has_auto_mentor);
      setIsManager(user.has_manager);
      setAllowedOpenTasks(String(user.allowed_open_tasks ?? 0));
    }
  };

  const handleSave = async () => {
    if (!userKey) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateData: ManagerUserUpdate = {
        first_name: firstName || null,
        last_name: lastName || null,
        primary_email: email,
        is_disabled: isDisabled,
        is_beta: isBeta,
        // Assistant flags and claim limits are only shown for assistants
        ...(user?.is_beta && {
          is_mentor: isMentor,
          auto_mentor: autoMentor,
          allowed_open_tasks: Number(allowedOpenTasks),
        }),
        // Only sudo can modify is_ic and the manager role
        ...(currentUserHasSudo && { is_ic: isIc }),
        ...(currentUserHasSudo && user?.is_beta && { is_manager: isManager }),
      };

      const updatedUser = await managerApi.updateUser(userKey, updateData);
      setUser(updatedUser);
      setSuccessMessage('User updated successfully');
      navigate(`/manager/user/${userKey}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Clear the claim cooldown - saves immediately
  const handleClearCooldown = async () => {
    if (!userKey) return;
    setClearingCooldown(true);
    setError(null);
    try {
      const updatedUser = await managerApi.updateUser(userKey, { clear_date_next_claim: true });
      setUser(updatedUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cooldown');
    } finally {
      setClearingCooldown(false);
    }
  };

  // Handle role toggle - saves immediately
  const handleRoleToggle = async (role: string) => {
    if (!user?.key || !userKey) return;

    setSavingRole(role);
    setError(null);

    try {
      const hasRole = user.roles.includes(role);
      if (hasRole) {
        await managerApi.revokeRole(user.key, role);
      } else {
        await managerApi.grantRole(user.key, role);
      }

      // Refresh user data to get updated roles
      const updatedUser = await managerApi.getUser(userKey);
      setUser(updatedUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setSavingRole(null);
    }
  };

  if (!user) {
    return null;
  }

  const userName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.primary_email;
  const cooldownUntil = user.date_next_claim ? new Date(user.date_next_claim) : null;
  const cooldownActive = cooldownUntil !== null && cooldownUntil > new Date();

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Profile Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Account Status
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isDisabled}
                  onChange={(e) => setIsDisabled(e.target.checked)}
                />
              }
              label="Disabled (cannot log in or work)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isBeta}
                  onChange={(e) => setIsBeta(e.target.checked)}
                />
              }
              label="Is Beta (approved assistant)"
            />
            {currentUserHasSudo && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isIc}
                    onChange={(e) => setIsIc(e.target.checked)}
                  />
                }
                label="Is IC (non-manager)"
              />
            )}
          </Box>

          {user.is_beta && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Assistant Flags
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                <FormControlLabel
                  control={<Switch checked={isMentor} onChange={(e) => setIsMentor(e.target.checked)} />}
                  label="Mentor (reviews other assistants' responses)"
                />
                <FormControlLabel
                  control={<Switch checked={autoMentor} onChange={(e) => setAutoMentor(e.target.checked)} />}
                  label="Auto-mentor (responses skip mentor review)"
                />
                {currentUserHasSudo && (
                  <FormControlLabel
                    control={<Switch checked={isManager} onChange={(e) => setIsManager(e.target.checked)} />}
                    label="Manager"
                  />
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Claim Limits
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {user.has_manager
                  ? `${userName} is a manager, so these limits don't apply.`
                  : `Claims by ${userName} beyond these limits are refused.`}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label="Max open tasks"
                  type="number"
                  value={allowedOpenTasks}
                  onChange={(e) => setAllowedOpenTasks(e.target.value)}
                  helperText={`0 = default (${DEFAULT_ALLOWED_OPEN_TASKS})`}
                  slotProps={{ htmlInput: { min: 0, step: 1 } }}
                  sx={{ flex: '1 1 200px' }}
                />
              </Box>

              {cooldownActive ? (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  action={
                    <Button color="inherit" size="small" onClick={handleClearCooldown} disabled={clearingCooldown}>
                      Clear
                    </Button>
                  }
                >
                  Claiming paused until {cooldownUntil.toLocaleString()}
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No claim cooldown.
                </Typography>
              )}
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            User Roles {!currentUserHasSudo && '(View Only)'}
          </Typography>

          {currentUserHasSudo ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toggle roles on/off. Changes are saved immediately.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                {EDITABLE_ROLES.map((role) => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Switch
                        checked={user.roles?.includes(role) ?? false}
                        onChange={() => handleRoleToggle(role)}
                        disabled={savingRole === role}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ROLE_LABELS[role]}
                        {savingRole === role && <CircularProgress size={16} />}
                      </Box>
                    }
                  />
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Sudo: {user.has_sudo ? 'Yes' : 'No'} (cannot be edited here)
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {user.roles && user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <Chip
                    key={role}
                    label={ROLE_LABELS[role] || role}
                    color={getRoleColor(role)}
                    size="small"
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No roles assigned
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                resetForm();
                navigate(`/manager/user/${userKey}`);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>

      {userKey && <ManagerUserBannedAssistants userKey={userKey} clientName={userName} />}
    </>
  );
});
